import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';
import { ComponentType, useEffect } from 'react';

import useDocuments from '../../hooks/useDocuments';
import usePlaceholderItems from '../../hooks/usePlaceholderItems';
import { getPrefixedProps } from '../../lib/utils';

import { DocumentFeaturedCardProps } from '../cards/DocumentFeaturedCard';
import * as CardComponents from '../cards/documents';

import styles from '../../styles/headers/featured-list-header.module.css';

export interface FeaturedListHeaderProps extends DocumentFeaturedCardProps {
    brand?: Brand | null;
    query?: Record<string, unknown> | null;
    items?: DocumentSnippet[] | null;
    count?: number;
    withoutLoader?: boolean;
    cardType?: string | null;
    cardTheme?: CardTheme | LegacyCardTheme | null;
    cardWith?: string[] | null;
    cardWithout?: string[] | null;
    cardRatio?: string | null;
    cardLinkQuery?: Record<string, unknown> | null;
    imageFetchPriority?: 'high' | 'low' | 'auto' | null;
    className?: string | null;
    innerClassName?: string | null;
    cardClassName?: string | null;
    labelClassName?: string | null;
    imageContainerClassName?: string | null;
    onLoaded?: ((items: DocumentSnippet[]) => void) | null;
}

const FeaturedListHeader = ({
    brand = null,
    query = null,
    items: providedItems = null,
    count = 2,
    withoutLoader = false,
    cardType = null,
    cardTheme = null,
    cardWith = null,
    cardWithout = null,
    cardRatio = null,
    cardLinkQuery = null,
    imageFetchPriority = null,
    className = null,
    innerClassName = null,
    cardClassName = null,
    labelClassName = null,
    imageContainerClassName = null,
    onLoaded = null,
    ...props
}: FeaturedListHeaderProps) => {
    const { handle: brandHandle = null } = brand || {};
    const { items: loadedItems } = useDocuments(
        {
            count,
            ...query,
        },
        {
            enabled: !withoutLoader,
        },
    );
    // The query returns null items when it does not load (withoutLoader)
    const items = loadedItems !== null ? loadedItems : providedItems;

    useEffect(() => {
        if (items !== null && onLoaded !== null) {
            onLoaded(items);
        }
    }, [items, onLoaded]);

    const placeholderItems = usePlaceholderItems(count);

    const FeaturedCard = getComponentFromName(CardComponents, cardType, CardComponents.Featured);

    return (
        <div
            className={classNames([
                styles.container,
                styles[camelCase(brandHandle)],
                styles[camelCase(cardTheme)],
                className,
            ])}
        >
            <div className={classNames([styles.inner, innerClassName])}>
                {(items || placeholderItems).map((it, index) => {
                    const itemTheme = cardTheme || ['left-center', 'right-center'][index % 2];
                    return (
                        <FeaturedCard
                            key={`item-${index}`}
                            placeholder={it === null}
                            theme={itemTheme}
                            ratio={cardRatio}
                            className={classNames([
                                styles.item,
                                styles[camelCase(itemTheme)],
                                {
                                    [styles.isOver]:
                                        itemTheme !== null && itemTheme.indexOf('over') !== -1,
                                },
                                cardClassName,
                            ])}
                            imageLoading={null}
                            imageFetchPriority={imageFetchPriority}
                            innerClassName={styles.itemInner}
                            labelClassName={classNames([styles.itemLabel, labelClassName])}
                            imageContainerClassName={classNames([
                                styles.imageContainer,
                                imageContainerClassName,
                            ])}
                            linkQuery={cardLinkQuery}
                            imageClassName={styles.image}
                            titleClassName={styles.title}
                            withBreakpoints
                            {...getPrefixedProps(cardWith, 'with')}
                            {...getPrefixedProps(cardWithout, 'without')}
                            {...props}
                            {...(it || {})}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default FeaturedListHeader;
